import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Box, Paper, useTheme, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CodeIcon from '@mui/icons-material/Code'
import TableViewIcon from '@mui/icons-material/TableView'
import CheckIcon from '@mui/icons-material/Check'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import yaml from 'js-yaml'
import FormRenderer from './FormRenderer'
import ConversationAlert from './ConversationAlert'

// Helper functions (defined outside component to avoid recreating on each render)
const formatPropertyName = (propertyName) => {
  return propertyName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const shouldHideValue = (value) => {
  return value === null || value === "" || value === 0
}

const isSimpleObject = (obj) => {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return false
  }

  return Object.values(obj).every(value =>
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
}

const isSimpleObjectArray = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return false
  }

  const firstKeys = Object.keys(arr[0] || {}).sort()
  return arr.every(item =>
    isSimpleObject(item) &&
    Object.keys(item).sort().join(',') === firstKeys.join(',')
  )
}

// LLMs occasionally emit the agentflow_alert block without its ``` fence (observed
// in production). Wrap bare blocks — an `agentflow_alert` line followed by YAML
// lines — in fences so they render as alerts. Content inside existing fences is
// left untouched. While streaming, a bare block still growing at the end of the
// content is hidden instead of wrapped, so partial alerts don't flash in.
const normalizeBareAlertBlocks = (content, isStreaming = false) => {
  if (!content || !content.includes('agentflow_alert')) return content

  const lines = content.split('\n')
  const out = []
  let inFence = false
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (/^\s*```/.test(line)) {
      inFence = !inFence
      out.push(line)
      continue
    }
    if (!inFence && line.trim() === 'agentflow_alert') {
      const block = []
      let j = i + 1
      while (j < lines.length && (/^(variant|title|body|actions):/.test(lines[j]) || /^[ \t]+\S/.test(lines[j]))) {
        block.push(lines[j])
        j++
      }
      if (block.length) {
        if (isStreaming && j === lines.length) {
          return out.join('\n') // block may still be streaming — hide it for now
        }
        out.push('```agentflow_alert', ...block, '```')
        i = j - 1
        continue
      }
      if (isStreaming && i === lines.length - 1) {
        return out.join('\n') // lone marker line at stream tail — hide it
      }
    }
    out.push(line)
  }
  return out.join('\n')
}

// While streaming, an ```agentflow_alert fence that hasn't been closed yet is
// hidden entirely — rendering it would flash a partial alert (or its raw YAML).
// While streaming, an unclosed code fence at the tail is still arriving — its
// partial content (raw YAML/JSON for forms, alerts, quick_replies, etc., or an
// as-yet-unrecognised language) would flash as a raw code block until the closing
// ``` lands. Scan fence markers; if one is still open at the end, hide from its
// opening line onward so only complete fences ever render.
const stripUnclosedFence = (content) => {
  const lines = content.split('\n')
  let openLine = -1
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*```/.test(lines[i])) {
      openLine = openLine === -1 ? i : -1 // toggle open/close on each fence marker
    }
  }
  return openLine === -1 ? content : lines.slice(0, openLine).join('\n')
}

// The model sometimes re-emits an identical agentflow_alert across auto-invoke rounds;
// token-by-token streaming concatenates them into one message, often fused as
// ``````agentflow_alert (a closing fence abutting the next opening fence with no newline).
// Split the fused boundary back into two fences, then collapse consecutive byte-identical
// alert blocks to one. Distinct alerts (different content, or separated by non-whitespace)
// are left untouched. The persisted thread is already de-duped server-side; this keeps the
// live token stream clean too.
const collapseDuplicateAlertBlocks = (content) => {
  if (!content || !content.includes('agentflow_alert')) return content
  // Re-split a fused close+reopen (```<ws>```agentflow_alert) into a proper boundary.
  const split = content.replace(/```\s*```agentflow_alert/g, '```\n```agentflow_alert')
  // Collapse runs of an alert block immediately repeated (whitespace-separated only).
  return split.replace(/(```agentflow_alert\n[\s\S]*?\n```)(?:\s*\1)+/g, '$1')
}

const isComplexTableObject = (obj) => {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return false
  }

  return Object.values(obj).some(value =>
    typeof value === 'object' && value !== null
  )
}

// JSON Table Toggle Component (defined outside to preserve state across re-renders)
const JSONTableToggle = React.memo(({ jsonObj, formattedJSON, isDark, theme }) => {
  const [showJSON, setShowJSON] = useState(false)

  const filteredEntries = Object.entries(jsonObj).filter(([key, value]) => !shouldHideValue(value))

  return (
    <Box sx={{ my: 2 }}>
      {!showJSON ? (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                    Property
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEntries.map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {formatPropertyName(key)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {typeof value === 'string' ? (
                        `"${value}"`
                      ) : (
                        String(value)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<CodeIcon />}
              onClick={() => setShowJSON(true)}
              sx={{ textTransform: 'none' }}
            >
              View JSON
            </Button>
          </Box>
        </>
      ) : (
        <>
          <SyntaxHighlighter
            style={isDark ? oneDark : oneLight}
            language="json"
            PreTag="div"
            customStyle={{
              borderRadius: theme.shape.borderRadius,
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {formattedJSON}
          </SyntaxHighlighter>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<TableViewIcon />}
              onClick={() => setShowJSON(false)}
              sx={{ textTransform: 'none' }}
            >
              View Table
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if the actual data changed
  return prevProps.formattedJSON === nextProps.formattedJSON &&
    prevProps.isDark === nextProps.isDark
})

const MessageContent = ({ content, sx, isUserMessage = false, onFormSubmit, formDataByFormId = {}, onFormFieldChange, isHistoricalQuickReply = false, selectedReply = null, isStreaming = false }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // Store toggle state at MessageContent level (survives re-renders)
  const [jsonViewStates, setJsonViewStates] = React.useState({})

  const toggleJsonView = useCallback((blockId) => {
    setJsonViewStates(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }))
  }, [])

  // Store memoized form field change handlers per formId to prevent FormRenderer from remounting on every keystroke
  const formFieldChangeHandlersRef = React.useRef({})
  const emptyFormDataRef = React.useRef({}) // Stable empty object reference
  const formDataByFormIdRef = React.useRef(formDataByFormId) // Store current formData in ref to avoid stale closures
  const parsedFormSpecsRef = React.useRef({}) // Cache parsed form specs to avoid recreating on every render

  // Update ref when formDataByFormId changes
  React.useEffect(() => {
    formDataByFormIdRef.current = formDataByFormId
  }, [formDataByFormId])

  const getFormFieldChangeHandler = useCallback((formId) => {
    // Return existing handler if we have one for this formId
    if (!formFieldChangeHandlersRef.current[formId]) {
      formFieldChangeHandlersRef.current[formId] = (fieldId, value) => {
        if (onFormFieldChange) {
          onFormFieldChange(formId, fieldId, value)
        }
      }
    }
    return formFieldChangeHandlersRef.current[formId]
  }, [onFormFieldChange])

  // Clear handlers when onFormFieldChange changes
  React.useEffect(() => {
    formFieldChangeHandlersRef.current = {}
  }, [onFormFieldChange])

  // Check if content looks like JSON
  const isJSON = (str) => {
    try {
      JSON.parse(str)
      return true
    } catch {
      return false
    }
  }

  // Check if content contains HTML demarcation (like ```html blocks or direct HTML)
  const detectHTML = (content) => {
    const htmlBlocks = []

    // 1. Look for HTML code blocks in markdown (```html ... ```)
    const htmlBlockRegex = /```html\n([\s\S]*?)\n```/gi
    let match
    while ((match = htmlBlockRegex.exec(content)) !== null) {
      htmlBlocks.push({
        content: match[1],
        fullMatch: match[0],
        start: match.index,
        end: match.index + match[0].length,
        type: 'markdown'
      })
    }

    // 2. Look for direct HTML content (<!DOCTYPE html>...content...</html> or <html>...content...</html>)
    // Match from <!DOCTYPE html> or <html> to </html>
    const directHTMLRegex = /(?:<!DOCTYPE html>[\s\S]*?<html[\s\S]*?>|<html[\s\S]*?>)([\s\S]*?)<\/html>/gi
    while ((match = directHTMLRegex.exec(content)) !== null) {
      // Check if this match overlaps with any markdown blocks (avoid double-processing)
      const overlaps = htmlBlocks.some(block =>
        (match.index >= block.start && match.index < block.end) ||
        (match.index + match[0].length > block.start && match.index + match[0].length <= block.end)
      )

      if (!overlaps) {
        htmlBlocks.push({
          content: match[0], // Full HTML including tags
          fullMatch: match[0],
          start: match.index,
          end: match.index + match[0].length,
          type: 'direct'
        })
      }
    }

    // Sort by position in content
    htmlBlocks.sort((a, b) => a.start - b.start)

    return htmlBlocks.length > 0 ? htmlBlocks : null
  }


  // Use the helper functions defined outside the component

  const renderJSONWithTabs = (jsonObj, formattedJSON) => {
    // Create stable ID for this JSON block
    const blockId = `json-${JSON.stringify(jsonObj).substring(0, 50)}`
    const showJSON = jsonViewStates[blockId] || false

    return (
      <Box sx={{ my: 2 }}>
        {!showJSON ? (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                      Property
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(jsonObj)
                    .filter(([key, value]) => !shouldHideValue(value))
                    .map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                          {formatPropertyName(key)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {typeof value === 'string' ? `"${value}"` : String(value)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<CodeIcon />}
                onClick={() => toggleJsonView(blockId)}
                sx={{ textTransform: 'none' }}
              >
                View JSON
              </Button>
            </Box>
          </>
        ) : (
          <>
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language="json"
              PreTag="div"
              customStyle={{
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {formattedJSON}
            </SyntaxHighlighter>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<TableViewIcon />}
                onClick={() => toggleJsonView(blockId)}
                sx={{ textTransform: 'none' }}
              >
                View Table
              </Button>
            </Box>
          </>
        )}
      </Box>
    )
  }

  // Render array of objects with accordion for multiple items - now a proper component
  const ArrayAccordionToggle = ({ jsonArray, formattedJSON, isDark, theme, formatPropertyName, shouldHideValue }) => {
    const [showJSON, setShowJSON] = useState(false)

    return (
      <Box sx={{ my: 2 }}>
        {!showJSON ? (
          <>
            <Box>
              {jsonArray.map((item, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">
                      Item {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                              Property
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                              Value
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(item)
                            .filter(([key, value]) => !shouldHideValue(value))
                            .map(([key, value]) => (
                              <TableRow key={key}>
                                <TableCell
                                  sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {formatPropertyName(key)}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                  {typeof value === 'string' ? (
                                    `"${value}"`
                                  ) : (
                                    String(value)
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<CodeIcon />}
                onClick={() => setShowJSON(true)}
                sx={{ textTransform: 'none' }}
              >
                View JSON
              </Button>
            </Box>
          </>
        ) : (
          <>
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language="json"
              PreTag="div"
              customStyle={{
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {formattedJSON}
            </SyntaxHighlighter>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<TableViewIcon />}
                onClick={() => setShowJSON(false)}
                sx={{ textTransform: 'none' }}
              >
                View Table
              </Button>
            </Box>
          </>
        )}
      </Box>
    )
  }

  const renderArrayWithAccordion = (jsonArray, formattedJSON) => {
    if (jsonArray.length === 1) {
      // Single item - show as normal table
      return renderJSONWithTabs(jsonArray[0], JSON.stringify(jsonArray[0], null, 2))
    }

    return <ArrayAccordionToggle
      jsonArray={jsonArray}
      formattedJSON={formattedJSON}
      isDark={isDark}
      theme={theme}
      formatPropertyName={formatPropertyName}
      shouldHideValue={shouldHideValue}
    />
  }

  // Render complex object with 2-column layout - now a proper component
  const ComplexObjectToggle = ({ jsonObj, formattedJSON, isDark, theme, formatPropertyName, shouldHideValue, isSimpleObjectArray, isSimpleObject }) => {
    const [showJSON, setShowJSON] = useState(false)

    const renderValue = (value) => {
      if (value === null) {
        return (
          <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            null
          </Box>
        )
      }

      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          // Check if it's an array of objects suitable for table display
          if (isSimpleObjectArray(value)) {
            // Render array of objects as multiple tables
            return (
              <Box sx={{ mt: 1 }}>
                {value.map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 'bold',
                        display: 'block',
                        mb: 1,
                        color: 'text.secondary'
                      }}
                    >
                      Item {index + 1} of {value.length}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100', fontSize: '0.75rem' }}>
                              Property
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100', fontSize: '0.75rem' }}>
                              Value
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(item)
                            .filter(([nestedKey, nestedValue]) => !shouldHideValue(nestedValue))
                            .map(([nestedKey, nestedValue]) => (
                              <TableRow key={nestedKey}>
                                <TableCell
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    py: 0.5,
                                    px: 1
                                  }}
                                >
                                  {formatPropertyName(nestedKey)}
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', py: 0.5, px: 1 }}>
                                  {typeof nestedValue === 'string' ? (
                                    nestedValue.length > 100 ? (
                                      <Box sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                        {nestedValue}
                                      </Box>
                                    ) : (
                                      `"${nestedValue}"`
                                    )
                                  ) : (
                                    String(nestedValue)
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Box>
            )
          } else {
            // Fallback to JSON for complex arrays
            return (
              <SyntaxHighlighter
                style={isDark ? oneDark : oneLight}
                language="json"
                PreTag="div"
                customStyle={{
                  borderRadius: theme.shape.borderRadius,
                  fontSize: '0.75rem',
                  margin: 0,
                  padding: '8px',
                }}
              >
                {JSON.stringify(value, null, 2)}
              </SyntaxHighlighter>
            )
          }
        } else if (isSimpleObject(value)) {
          // Simple nested object - show as mini table
          return (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableBody>
                  {Object.entries(value)
                    .filter(([nestedKey, nestedValue]) => !shouldHideValue(nestedValue))
                    .map(([nestedKey, nestedValue]) => (
                      <TableRow key={nestedKey}>
                        <TableCell
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            py: 0.5,
                            px: 1
                          }}
                        >
                          {formatPropertyName(nestedKey)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', py: 0.5, px: 1 }}>
                          {typeof nestedValue === 'string' ? (
                            `"${nestedValue}"`
                          ) : (
                            String(nestedValue)
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        } else {
          // Complex nested object - show as JSON
          return (
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language="json"
              PreTag="div"
              customStyle={{
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.75rem',
                margin: 0,
                padding: '8px',
              }}
            >
              {JSON.stringify(value, null, 2)}
            </SyntaxHighlighter>
          )
        }
      }

      return (
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {typeof value === 'string' ? `"${value}"` : String(value)}
        </Box>
      )
    }

    return (
      <Box sx={{ my: 2 }}>
        {!showJSON ? (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                      Property
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: isDark ? 'grey.800' : 'grey.100' }}>
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(jsonObj)
                    .filter(([key, value]) => !shouldHideValue(value))
                    .map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            verticalAlign: 'top'
                          }}
                        >
                          {formatPropertyName(key)}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {renderValue(value)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<CodeIcon />}
                onClick={() => setShowJSON(true)}
                sx={{ textTransform: 'none' }}
              >
                View JSON
              </Button>
            </Box>
          </>
        ) : (
          <>
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language="json"
              PreTag="div"
              customStyle={{
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {formattedJSON}
            </SyntaxHighlighter>
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                startIcon={<TableViewIcon />}
                onClick={() => setShowJSON(false)}
                sx={{ textTransform: 'none' }}
              >
                View Table
              </Button>
            </Box>
          </>
        )}
      </Box>
    )
  }

  const renderComplexObject = (jsonObj, formattedJSON) => {
    return <ComplexObjectToggle
      jsonObj={jsonObj}
      formattedJSON={formattedJSON}
      isDark={isDark}
      theme={theme}
      formatPropertyName={formatPropertyName}
      shouldHideValue={shouldHideValue}
      isSimpleObjectArray={isSimpleObjectArray}
      isSimpleObject={isSimpleObject}
    />
  }

  // Render HTML content safely in an iframe using srcdoc (avoids blob URL sandbox errors)
  const HTMLPreviewToggle = React.memo(({ htmlContent, isDark, theme }) => {
    const [showPreview, setShowPreview] = useState(true)

    return (
      <Box sx={{ my: 2 }}>
        {showPreview ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                📧 Email Preview
                <Button
                  size="small"
                  startIcon={<CodeIcon />}
                  onClick={() => setShowPreview(false)}
                  sx={{ textTransform: 'none', ml: 'auto' }}
                >
                  View HTML Source
                </Button>
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  border: 2,
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                <iframe
                  srcDoc={htmlContent}
                  style={{
                    width: '100%',
                    height: '400px',
                    border: 'none',
                    backgroundColor: 'white',
                  }}
                  sandbox=""
                  title="HTML Email Preview"
                />
              </Paper>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">HTML Source</Typography>
              <Button
                size="small"
                onClick={() => setShowPreview(true)}
                sx={{ textTransform: 'none' }}
              >
                View Preview
              </Button>
            </Box>
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language="html"
              PreTag="div"
              customStyle={{
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                maxHeight: '400px',
                overflow: 'auto',
              }}
            >
              {htmlContent}
            </SyntaxHighlighter>
          </>
        )}
      </Box>
    )
  }, (prevProps, nextProps) => {
    // Only re-render if htmlContent or theme mode changed
    return prevProps.htmlContent === nextProps.htmlContent &&
      prevProps.isDark === nextProps.isDark
  })

  const renderHTMLPreview = (htmlContent) => {
    return <HTMLPreviewToggle
      htmlContent={htmlContent}
      isDark={isDark}
      theme={theme}
    />
  }

  const components = useMemo(() => ({
    // Code blocks
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '')
      const language = match ? match[1] : ''

      if (!inline && language === 'form') {
        // Form is rendered in overlay - return null to completely skip inline rendering
        return null
      }

      if (!inline && language === 'quick_replies') {
        // Live: rendered as clickable chips above the input (skip inline).
        // History (already answered): render options read-only, chosen one highlighted.
        if (!isHistoricalQuickReply) {
          return null
        }
        try {
          const options = yaml.load(String(children).replace(/\n$/, ''))
          if (!Array.isArray(options)) return null
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, my: 1 }}>
              {options.map((opt) => {
                const label = String(opt)
                const isChosen = selectedReply != null && label === selectedReply
                return (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    icon={isChosen ? <CheckIcon /> : undefined}
                    color={isChosen ? 'primary' : 'default'}
                    variant={isChosen ? 'filled' : 'outlined'}
                    sx={{ borderRadius: '16px', fontSize: '0.875rem', opacity: isChosen ? 1 : 0.7 }}
                  />
                )
              })}
            </Box>
          )
        } catch {
          // Malformed YAML — fail safe, render nothing (matches prior behavior).
          return null
        }
      }

      if (!inline && language === 'submitted_form_data') {
        try {
          const content = String(children).replace(/\n$/, '')
          console.log('📋 Parsing submitted form data:', content.substring(0, 200))
          const data = JSON.parse(content)

          if (data && data.form_spec && data.values) {
            // Parse form spec if it's a string
            const formSpec = typeof data.form_spec === 'string'
              ? yaml.load(data.form_spec)
              : data.form_spec

            console.log('✅ Form parsed successfully:', formSpec.title || 'Untitled Form')
            return (
              <Box sx={{ my: 2 }}>
                <FormRenderer
                  formSpec={formSpec}
                  formData={data.values}
                  readOnly={true}
                  onFieldChange={() => { }}
                />
              </Box>
            )
          } else if (data && data.values) {
            // Handle case where only values are present (no form_spec)
            // This can happen with older messages or certain submission flows
            console.warn('⚠️ Submitted form data missing form_spec, showing values only')

            return (
              <Box sx={{ my: 2 }}>
                <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                    Form Submission
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {Object.entries(data.values).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                              {formatPropertyName(key)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem' }}>
                              {Array.isArray(value) ? value.join(', ') : String(value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>
            )
          } else {
            console.warn('⚠️ Submitted form data missing required fields:', {
              hasFormSpec: !!data?.form_spec,
              hasValues: !!data?.values,
              data
            })
          }
        } catch (error) {
          console.error('❌ Failed to render submitted form:', error, 'Content:', String(children).substring(0, 200))
        }

        // Fallback: show that a form was submitted but couldn't be parsed
        return (
          <Box sx={{ my: 2, p: 2, bgcolor: isDark ? 'grey.900' : 'grey.100', borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              📋 Form submitted (unable to display details)
            </Typography>
          </Box>
        )
      }

      if (!inline && language === 'agentflow_banner') {
        return null
      }

      if (!inline && language === 'agentflow_alert') {
        try {
          const alert = yaml.load(String(children).replace(/\n$/, ''))
          // YAML can parse unquoted values into Dates/booleans — only render string content
          const hasStringBody = alert && typeof alert === 'object' && typeof alert.body === 'string'
          const hasValidTitle = alert && (alert.title == null || typeof alert.title === 'string')
          const hasValidActions = alert && (alert.actions == null || Array.isArray(alert.actions))
          if (hasStringBody && hasValidTitle && hasValidActions) {
            return <ConversationAlert alert={alert} />
          }
        } catch {
          // Malformed YAML — fall through to the code-block fallback below
        }
        // Invalid alert spec: fall through so the content stays visible as a code block
      }

      if (!inline && language === 'html') {
        const htmlContent = String(children).replace(/\n$/, '')
        return renderHTMLPreview(htmlContent)
      }

      if (!inline && language === 'json') {
        const jsonContent = String(children).replace(/\n$/, '')

        try {
          const jsonObj = JSON.parse(jsonContent)

          // Check what type of JSON structure we have
          if (isSimpleObjectArray(jsonObj)) {
            return renderArrayWithAccordion(jsonObj, jsonContent)
          } else if (isSimpleObject(jsonObj)) {
            return renderJSONWithTabs(jsonObj, jsonContent)
          } else if (isComplexTableObject(jsonObj)) {
            return renderComplexObject(jsonObj, jsonContent)
          }
        } catch (error) {
          // Silent fallback to regular JSON syntax highlighting
        }

        // Fallback to regular JSON syntax highlighting
        return (
          <Box sx={{ my: 2 }}>
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language="json"
              PreTag="div"
              customStyle={{
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              {...props}
            >
              {jsonContent}
            </SyntaxHighlighter>
          </Box>
        )
      }

      if (!inline && language === 'text') {
        // Special handling for text blocks (like EDI content) - preserve formatting
        return (
          <Box sx={{ my: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: isDark ? 'grey.900' : 'grey.50',
                border: 1,
                borderColor: isDark ? 'grey.700' : 'grey.300',
                borderRadius: theme.shape.borderRadius,
              }}
            >
              <Box
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  color: isDark ? 'grey.100' : 'grey.900',
                }}
              >
                {String(children).replace(/\n$/, '')}
              </Box>
            </Paper>
          </Box>
        )
      }

      if (!inline && language) {
        return (
          <Box sx={{ my: 2 }}>
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language={language}
              PreTag="div"
              customStyle={{
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          </Box>
        )
      }

      return (
        <Box
          component="code"
          sx={{
            bgcolor: isUserMessage
              ? 'rgba(255, 255, 255, 0.2)'
              : isDark ? 'grey.800' : 'grey.100',
            color: isUserMessage
              ? 'inherit'
              : isDark ? 'grey.100' : 'grey.900',
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.875rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
          {...props}
        >
          {children}
        </Box>
      )
    },

    // Blockquotes
    blockquote({ children, ...props }) {
      return (
        <Paper
          sx={{
            borderLeft: 4,
            borderLeftColor: isUserMessage ? 'rgba(255, 255, 255, 0.5)' : 'primary.main',
            pl: 2,
            py: 1,
            my: 2,
            bgcolor: isUserMessage
              ? 'rgba(255, 255, 255, 0.1)'
              : isDark ? 'grey.900' : 'grey.50',
            fontStyle: 'italic',
            color: isUserMessage ? 'inherit' : undefined,
          }}
          elevation={0}
          {...props}
        >
          {children}
        </Paper>
      )
    },

    // Tables
    table({ children, ...props }) {
      return (
        <Box sx={{ overflow: 'auto', my: 2 }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              border: 1,
              borderColor: 'divider',
              '& th, & td': {
                border: 1,
                borderColor: 'divider',
                px: 2,
                py: 1,
                textAlign: 'left',
              },
              '& th': {
                bgcolor: isDark ? 'grey.800' : 'grey.100',
                fontWeight: 'bold',
              },
              '& tr:nth-of-type(even)': {
                bgcolor: isDark ? 'grey.900' : 'grey.50',
              },
            }}
            {...props}
          >
            {children}
          </Box>
        </Box>
      )
    },

    // Links
    a({ children, href, ...props }) {
      return (
        <Box
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: isUserMessage ? 'primary.dark' : 'primary.main',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none',
            },
          }}
          {...props}
        >
          {children}
        </Box>
      )
    },

    // Images
    img({ src, alt, ...props }) {
      return (
        <Box
          component="img"
          src={src}
          alt={alt || ''}
          sx={{
            maxWidth: '100%',
            maxHeight: 400,
            borderRadius: 1,
            my: 1,
            display: 'block',
          }}
          {...props}
        />
      )
    },

    // Paragraphs
    p({ children, ...props }) {
      return (
        <Box component="p" sx={{ mb: 2, lineHeight: 1.6, fontWeight: 400 }} {...props}>
          {children}
        </Box>
      )
    },

    // Lists
    ul({ children, ordered, ...props }) {
      // Filter out 'ordered' prop - it's metadata from ReactMarkdown, not a DOM attribute
      return (
        <Box component="ul" sx={{ mb: 2, pl: 3 }} {...props}>
          {children}
        </Box>
      )
    },

    ol({ children, ordered, ...props }) {
      // Filter out 'ordered' prop - it's metadata from ReactMarkdown, not a DOM attribute
      return (
        <Box component="ol" sx={{ mb: 2, pl: 3 }} {...props}>
          {children}
        </Box>
      )
    },

    li({ children, ordered, ...props }) {
      // Filter out 'ordered' prop - it's metadata from ReactMarkdown, not a DOM attribute
      return (
        <Box component="li" sx={{ mb: 0.5 }} {...props}>
          {children}
        </Box>
      )
    },

    // Headings
    h1({ children, ...props }) {
      return (
        <Box component="h1" sx={{ fontSize: '1.5rem', fontWeight: 400, mb: 2, mt: 3 }} {...props}>
          {children}
        </Box>
      )
    },

    h2({ children, ...props }) {
      return (
        <Box component="h2" sx={{ fontSize: '1.25rem', fontWeight: 400, mb: 2, mt: 3 }} {...props}>
          {children}
        </Box>
      )
    },

    h3({ children, ...props }) {
      return (
        <Box component="h3" sx={{ fontSize: '1.125rem', fontWeight: 400, mb: 1, mt: 2 }} {...props}>
          {children}
        </Box>
      )
    },

    // Horizontal rule
    hr({ ...props }) {
      return (
        <Box
          component="hr"
          sx={{
            border: 'none',
            borderTop: 1,
            borderColor: 'divider',
            my: 3,
          }}
          {...props}
        />
      )
    },
  }), [onFormSubmit, getFormFieldChangeHandler, isDark, theme, isUserMessage, jsonViewStates, toggleJsonView])

  // Collapse identical re-emitted alerts (fused/duplicated in the token stream),
  // hide any partially-streamed (unclosed) fence, then re-fence complete bare alerts
  content = collapseDuplicateAlertBlocks(content)
  if (isStreaming) {
    content = stripUnclosedFence(content)
  }
  content = normalizeBareAlertBlocks(content, isStreaming)

  // Check for HTML content first
  const detectedHTML = detectHTML(content)
  if (detectedHTML && detectedHTML.length > 0) {
    // Check if the entire content is just a single HTML block
    const contentTrimmed = content.trim()
    const isSingleHTMLBlock = detectedHTML.length === 1 &&
      (detectedHTML[0].start === 0 || contentTrimmed.startsWith(detectedHTML[0].fullMatch.trim())) &&
      (detectedHTML[0].end === content.length || contentTrimmed.endsWith(detectedHTML[0].fullMatch.trim()))

    if (isSingleHTMLBlock) {
      // Entire content is HTML - render directly
      return renderHTMLPreview(detectedHTML[0].content)
    }

    // Multiple HTML blocks or HTML mixed with other content - process and embed
    let processedContent = content

    // Process blocks in reverse order to maintain correct indices
    detectedHTML.reverse().forEach((htmlBlock, reverseIndex) => {
      const index = detectedHTML.length - 1 - reverseIndex

      // Create a placeholder that will be processed by markdown
      const placeholder = `\n\n**Email Preview ${index + 1}:**\n\`\`\`html\n${htmlBlock.content}\n\`\`\`\n\n`

      // Replace the original HTML block with the placeholder
      processedContent = processedContent.substring(0, htmlBlock.start) +
        placeholder +
        processedContent.substring(htmlBlock.end)
    })

    // Use the processed content with markdown
    content = processedContent
  }

  // Format JSON content
  if (isJSON(content)) {
    try {
      const jsonObj = JSON.parse(content)
      const formattedJSON = JSON.stringify(jsonObj, null, 2)

      // Check what type of JSON structure we have
      if (isSimpleObjectArray(jsonObj)) {
        return renderArrayWithAccordion(jsonObj, formattedJSON)
      } else if (isSimpleObject(jsonObj)) {
        return renderJSONWithTabs(jsonObj, formattedJSON)
      } else if (isComplexTableObject(jsonObj)) {
        return renderComplexObject(jsonObj, formattedJSON)
      }

      // Otherwise, show regular JSON syntax highlighting
      return (
        <SyntaxHighlighter
          style={isDark ? oneDark : oneLight}
          language="json"
          PreTag="div"
          customStyle={{
            borderRadius: theme.shape.borderRadius,
            fontSize: '0.875rem',
          }}
        >
          {formattedJSON}
        </SyntaxHighlighter>
      )
    } catch {
      // Fall through to markdown rendering
    }
  }

  return (
    <Box
      sx={{
        '& > *:first-of-type': {
          mt: 0,
        },
        '& > *:last-child': {
          mb: 0,
        },
        wordBreak: 'break-word',
        lineHeight: 1.6,
        color: isUserMessage ? 'inherit' : undefined,
        fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontWeight: 400,
        fontSize: '0.95rem',
        ...sx
      }}
    >
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm]}
        skipHtml={false}
      >
        {content}
      </ReactMarkdown>
    </Box>
  )
}

export default MessageContent